import React from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, Sparkles, Check, X } from 'lucide-react';
import { trackEvent } from '../lib/track';
import { useT, useLocalField } from '../i18n';

// Proactive "let us help you" offer shown on the unpaid preview when a parent
// looks hesitant (rapid page-flipping / scrolling early on — see the hesitation
// detector in Preview.jsx). Its job is to catch a wavering visitor before they
// bounce and hand them to a human on WhatsApp who can make the pictures
// print-ready. Framed as a warm free offer (never "you look confused"), so it
// still reads well if the trigger fires on someone who is merely skimming.
//
// Tapping the CTA opens the visitor's OWN WhatsApp to our number with a
// pre-filled, context-carrying message (child name + book) — they press send.
const WHATSAPP_NUMBER = '972505020178';

export default function PreviewHelpModal({ open, onClose, story, shareToken }) {
  const t = useT();
  const lf = useLocalField();
  if (!open) return null;

  const bookName = story?.template ? lf(story.template, 'nameAr') : '';
  const childName = (story?.personalization?.name || '').trim();

  const openWhatsApp = () => {
    // Only include the book/child lines when we actually have them, so the
    // message never ships an empty "الكتاب: —" line to the team.
    const lines = [t.previewHelpWaIntro];
    if (bookName || childName) {
      lines.push('');
      if (bookName) lines.push(t.previewHelpWaBook.replace('{book}', bookName));
      if (childName) lines.push(t.previewHelpWaChild.replace('{name}', childName));
    }
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join('\n'))}`;
    trackEvent('preview_help_whatsapp', { slug: shareToken || story?.shareToken, name: story?.template?.nameAr });
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose?.();
  };

  return createPortal((
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card preview-help-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" aria-label={t.previewHelpClose} onClick={onClose}>
          <X size={22} />
        </button>

        <div className="preview-help-icon" aria-hidden="true">
          <Sparkles size={26} />
        </div>

        <span className="preview-help-badge">
          <Sparkles size={13} aria-hidden="true" /> {t.previewHelpBadge}
        </span>
        <h3 className="modal-title preview-help-title">{t.previewHelpTitle}</h3>
        <p className="modal-text preview-help-text">{t.previewHelpText}</p>

        <ul className="preview-help-list">
          <li><span className="preview-help-check" aria-hidden="true"><Check size={14} /></span>{t.previewHelpBullet1}</li>
          <li><span className="preview-help-check" aria-hidden="true"><Check size={14} /></span>{t.previewHelpBullet2}</li>
          <li><span className="preview-help-check" aria-hidden="true"><Check size={14} /></span>{t.previewHelpBullet3}</li>
        </ul>

        <button type="button" className="preview-help-cta" onClick={openWhatsApp}>
          <MessageCircle size={20} aria-hidden="true" /> {t.previewHelpCta}
        </button>
        <p className="preview-help-reassure">{t.previewHelpReassure}</p>

        <button type="button" className="preview-help-dismiss" onClick={onClose}>
          {t.previewHelpDismiss}
        </button>
      </div>
    </div>
  ), document.body);
}
